import React, { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { AuditLogEntry } from '@kcdash/shared';
import { FileText, Search, Filter, Shield } from 'lucide-react';

export const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchAction, setSearchAction] = useState('');

  const loadAuditLogs = async () => {
    setIsLoading(true);
    try {
      const data = await api.getAuditLogs({
        action: searchAction || undefined,
        limit: 100,
      });
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err: any) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLogs();
  }, [searchAction]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-neutral-100">Security Audit Logs</h2>
          <p className="text-xs text-neutral-400 mt-1 font-mono">
            Immutable tracking of administrator operations, server lifecycle, and file changes
          </p>
        </div>

        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            placeholder="Filter by action (e.g. SERVER_START)..."
            value={searchAction}
            onChange={(e) => setSearchAction(e.target.value)}
            className="pl-8 pr-3 py-1.5 rounded-lg bg-panel-card border border-panel-border text-xs text-neutral-200 placeholder-neutral-500 focus:outline-none focus:border-brand-500 font-mono w-64"
          />
        </div>
      </div>

      <div className="bg-panel-card border border-panel-border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-panel-subtle/80 border-b border-panel-border text-neutral-400 uppercase select-none">
              <tr>
                <th className="px-5 py-3">Timestamp</th>
                <th className="px-5 py-3">User</th>
                <th className="px-5 py-3">Action</th>
                <th className="px-5 py-3">Server</th>
                <th className="px-5 py-3">IP Address</th>
                <th className="px-5 py-3">Metadata</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-panel-border/50 text-neutral-300">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-neutral-500">
                    Loading security audit trail...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-neutral-500">
                    No audit records matching query.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-panel-cardHover transition">
                    <td className="px-5 py-3 text-neutral-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 font-semibold text-neutral-200">{log.username}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 rounded bg-brand-950/70 border border-brand-800/80 text-brand-400 font-medium">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-neutral-400">{log.serverId || 'Global'}</td>
                    <td className="px-5 py-3 text-neutral-400">{log.ipAddress}</td>
                    <td className="px-5 py-3 text-neutral-500 truncate max-w-xs font-mono text-[11px]">
                      {log.metadata ? JSON.stringify(log.metadata) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
